import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import type { FunctionComponent, RefObject } from 'react';
import cx from 'classnames';
import { getScreenFromUrl, Analytics } from '@trello/atlassian-analytics';
import { DynamicButton } from '@trello/dynamic-tokens';
import { forNamespace } from '@trello/i18n';
import { Key, Scope, useShortcut } from '@trello/keybindings';
import { Button } from '@trello/nachos/button';
import { CloseIcon } from '@trello/nachos/icons/close';
import { FilterIcon } from '@trello/nachos/icons/filter';
import { Popover, usePopover, PopoverPlacement } from '@trello/nachos/popover';
import { useRecentlyUsedFeaturePreloader } from '@trello/recently-used-feature-preloader';
import { useSharedState } from '@trello/shared-state';
import { FilterPopoverTestIds } from '@trello/test-ids';
import { ErrorBoundary, ChunkLoadErrorBoundary } from '@trello/error-reporting';
import { ShortcutTooltip } from 'app/src/components/ShortcutTooltip';
import { ViewFiltersContext } from 'app/src/components/ViewFilters';
import type { CountableViewFilterParams } from 'app/src/components/ViewFilters';
import { mergeRefs } from 'app/src/mergeRefs';
import { FilterPopoverSkeleton } from './FilterPopoverSections/FilterPopoverSkeleton';
import { filteredCardsCountState } from './filteredCardsCountState';
import { useFilterPopoverAnalytics } from './useFilterPopoverAnalytics';
import styles from './FilterPopoverButton.less';

const format = forNamespace('filter popover');

// Filters to skip when calculating active filters and clearing filters.
export const SKIPPED_FILTERS: CountableViewFilterParams = ['boards', 'sort'];

export interface FilterPopoverButtonProps {
  /**
   * State styling is manually applied within this component, as it's considered
   * "active" when any filters are applied. Appearance is referenced directly in
   * our LESS styles as a result.
   * @default 'default'
   */
  appearance?: 'default' | 'transparent' | 'transparent-dark';
  isDisabled?: boolean;
  onLoad?: () => void;
}

export const FilterPopoverButton: FunctionComponent<FilterPopoverButtonProps> = ({
  appearance = 'default',
  isDisabled,
  onLoad,
}) => {
  const FilterPopoverAnalytics = useFilterPopoverAnalytics();
  const { viewFilters } = useContext(ViewFiltersContext);

  const [FilterPopover, trackFeatureUsage] = useRecentlyUsedFeaturePreloader({
    featureName: 'FilterPopover',
    useLazyComponentArgs: [
      () => import(/* webpackChunkName: "filter-popover" */ './FilterPopover'),
      { namedImport: 'FilterPopover' },
    ],
    onPreloadedCallback: onLoad,
  });

  const popoverResult = usePopover<HTMLButtonElement>({
    size: 'large',
    onShow: () => {
      FilterPopoverAnalytics.sendScreenEvent({
        name: 'filterPopoverInlineDialog',
        attributes: viewFilters.getCommonAttributes?.(),
      });
    },
    onHide: () => {
      FilterPopoverAnalytics.sendClosedComponentEvent({
        componentType: 'inlineDialog',
        componentName: 'filterPopoverInlineDialog',
        source: getScreenFromUrl(),
        attributes: viewFilters.getCommonAttributes?.(),
      });
    },
    placement: PopoverPlacement.BOTTOM_END,
  });

  useEffect(() => {
    viewFilters.setFilterPopoverResult?.(popoverResult);

    return () => {
      viewFilters.setFilterPopoverResult?.(null);
    };
    // eslint-disable-next-line @trello/exhaustive-deps
  }, []);

  const { toggle, triggerRef, popoverProps } = popoverResult;

  const onClickFilterCardsTooltipRef = useRef<() => void>();
  const togglePopover = useCallback(() => {
    onLoad?.();
    onClickFilterCardsTooltipRef.current?.();
    trackFeatureUsage();
    // We only want open clicks; close clicks aren't very interesting.
    if (!popoverProps.isVisible) {
      FilterPopoverAnalytics.sendClickedButtonEvent({
        buttonName: 'filterPopoverButton',
        source: getScreenFromUrl(),
        attributes: viewFilters.getCommonAttributes?.(),
      });
    }
    toggle();
  }, [
    FilterPopoverAnalytics,
    onLoad,
    popoverProps.isVisible,
    toggle,
    trackFeatureUsage,
    viewFilters,
  ]);

  useShortcut(togglePopover, { scope: Scope.BoardView, key: Key.f });

  const onClickClearFiltersTooltipRef = useRef<() => void>();
  const onClickClearFiltersButton = useCallback(async () => {
    const traceId = Analytics.startTask({
      taskName: 'remove-boardFilter',
      source: 'filterPopoverButton',
    });

    try {
      FilterPopoverAnalytics.sendClickedButtonEvent({
        buttonName: 'clearFiltersButton',
        source: getScreenFromUrl(),
        attributes: viewFilters.getCommonAttributes?.(),
      });

      viewFilters.resetFilters(SKIPPED_FILTERS);
      onClickClearFiltersTooltipRef.current?.();

      Analytics.taskSucceeded({
        taskName: 'remove-boardFilter',
        source: 'filterPopoverButton',
        traceId,
      });
    } catch (error) {
      Analytics.taskFailed({
        taskName: 'remove-boardFilter',
        source: 'filterPopoverButton',
        traceId,
        // @ts-expect-error
        error,
      });
    }
  }, [FilterPopoverAnalytics, viewFilters]);

  const isSavedView = viewFilters.contextType === 'savedView';
  const isFiltering = viewFilters.filters.isFiltering(
    viewFilters.contextType === 'singleBoard'
      ? SKIPPED_FILTERS
      : [
          ...SKIPPED_FILTERS,
          'title',
        ] /* skipping the 'title' filter for workspace views */,
  );
  const isPreviewFiltering = isSavedView && viewFilters.isFiltersPreviewActive;
  const isActive =
    popoverProps.isVisible || (isSavedView ? isPreviewFiltering : isFiltering);
  const showCardCountAndXButton = isSavedView
    ? isPreviewFiltering
    : isFiltering;

  const [cardsCountState] = useSharedState(filteredCardsCountState);

  // Button appearances of `transparent` and `transparent-dark` are being
  // deprecated, but this is a holdover, as we've built this button atop those
  // appearances. When the appearance is either of those, use the DynamicButton
  // component instead, to expedite the appearance cleanup.
  const ButtonComponent = appearance === 'default' ? Button : DynamicButton;

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-panorama',
        feature: 'Project Brita',
      }}
    >
      <div className={cx(styles.buttonGroup)} data-appearance={appearance}>
        <ShortcutTooltip shortcutText={format('filter cards')} shortcutKey="f">
          {({ ref: tooltipRef, onClick: onClickTooltip, ...tooltipProps }) => {
            const mergedRefs = mergeRefs(triggerRef, tooltipRef);
            onClickFilterCardsTooltipRef.current = onClickTooltip as typeof onClickFilterCardsTooltipRef.current;

            return (
              <ButtonComponent
                className={cx(
                  styles.filterButton,
                  styles[appearance],
                  isActive && styles.isActive,
                  isFiltering && styles.isFiltering,
                )}
                iconBefore={<FilterIcon size="small" />}
                isDisabled={isDisabled}
                onClick={togglePopover}
                ref={mergedRefs as RefObject<HTMLButtonElement>}
                data-testid={FilterPopoverTestIds.FilterPopoverButton}
                {...tooltipProps}
              >
                {format('filter')}
                {showCardCountAndXButton && (
                  <span
                    data-testid={
                      FilterPopoverTestIds.FilterPopoverButtonCardCount
                    }
                    className={cx(
                      styles.cardsCount,
                      cardsCountState.isLoading && styles.isLoadingCards,
                    )}
                  >
                    {cardsCountState.count}
                  </span>
                )}
              </ButtonComponent>
            );
          }}
        </ShortcutTooltip>
        {showCardCountAndXButton ? (
          <ShortcutTooltip
            shortcutText={format('clear filters')}
            shortcutKey="x"
          >
            {({ onClick: onClickTooltip, ...tooltipProps }) => {
              onClickClearFiltersTooltipRef.current = onClickTooltip as typeof onClickFilterCardsTooltipRef.current;
              return (
                <ButtonComponent
                  data-testid={FilterPopoverTestIds.FilterPopoverButtonX}
                  className={cx(styles.clearFiltersButton, styles[appearance])}
                  iconBefore={<CloseIcon size="small" />}
                  isDisabled={isDisabled}
                  onClick={onClickClearFiltersButton}
                  {...tooltipProps}
                />
              );
            }}
          </ShortcutTooltip>
        ) : null}
      </div>
      {popoverProps.isVisible && (
        <Popover
          title={format('filter')}
          dontOverlapAnchorElement
          {...popoverProps}
          data-testid={FilterPopoverTestIds.FilterPopoverContents}
        >
          <Suspense fallback={<FilterPopoverSkeleton />}>
            <ChunkLoadErrorBoundary
              fallback={<FilterPopoverSkeleton />}
              retryAfter={15000}
            >
              <FilterPopover viewFilters={viewFilters} />
            </ChunkLoadErrorBoundary>
          </Suspense>
        </Popover>
      )}
    </ErrorBoundary>
  );
};
